import { api, downloadApiFile } from '../api';
import { normalizeTransferTargets } from './TransferTargets';
import type {
  BeelineSipAccount,
  BrowserCall,
  BrowserPhoneConfig,
  BrowserPhoneSettings,
  BrowserPhoneUser,
  CallHistoryFilterOptions,
  CallStatistics,
  CallResultPayload,
  OperatorStatus,
  OutboundCallPayload,
  PbxStatus,
  PhoneContact,
  RecordingMaintenanceJob,
  RecordingRetentionStatus,
  UpdateRecordingRetentionPayload,
  TelephonyRoutePreview,
  TelephonyRoutingHistoryEntry,
  TelephonyRoutesState,
  TelephonyQualityDiagnostics,
  TelephonyAutoRepairResult,
  TelephonyBlacklistPayload,
  TelephonyBlacklistRule,
  WebRtcMediaQuality,
} from './types';
import type { MediaQualityReport } from './MediaQualityReporter';
import { callClientLinkPayload } from '../client-history/api';

export const telephonyApi = {
  config: () => api<BrowserPhoneConfig>('/telephony/browser/config'),
  status: () => api<PbxStatus>('/telephony/status'),
  browserSettings: () => api<BrowserPhoneSettings>('/telephony/browser/settings'),
  updateBrowserSettings: (body: Omit<BrowserPhoneSettings, 'secureTransport' | 'saved'>) =>
    api<BrowserPhoneSettings>('/telephony/browser/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  beelineSip: () => api<BeelineSipAccount>('/telephony/browser/beeline-sip'),
  updateBeelineSip: (body: Record<string, unknown>) => api<BeelineSipAccount>('/telephony/browser/beeline-sip', {
    method: 'PUT',
    body: JSON.stringify(body),
  }),
  validateBeelineSip: () => api<{ ready: boolean; status: string; message: string; pbx?: PbxStatus }>('/telephony/browser/beeline-sip/validate', { method: 'POST' }),
  provisionPbx: () => api('/telephony/browser/pbx/provision', { method: 'POST' }),
  pbxStatus: () => api<PbxStatus>('/telephony/browser/pbx/status'),
  exportDiagnostics: () => downloadApiFile('/telephony/browser/diagnostics/export'),
  autoRepair: () => api<TelephonyAutoRepairResult>('/telephony/browser/diagnostics/repair', { method: 'POST' }),
  users: () => api<BrowserPhoneUser[]>('/telephony/browser/users'),
  transferTargets: async () => normalizeTransferTargets(await api<unknown[]>('/telephony/browser/transfer-targets')),
  updateUser: (userId: string, body: Record<string, unknown>) => api<BrowserPhoneUser>(`/telephony/browser/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  }),
  regenerateUserCredentials: (userId: string) => api<{
    userId: string;
    extension: string;
    rotatedAt: string;
    registrationRevoked: boolean;
    state: 'ACTIVE' | 'REVOCATION_PENDING';
    message: string;
  }>(`/telephony/browser/users/${userId}/credentials/regenerate`, { method: 'POST' }),
  routes: () => api<TelephonyRoutesState>('/telephony/browser/routes'),
  routesHistory: () => api<TelephonyRoutingHistoryEntry[]>('/telephony/browser/routes/history?limit=50'),
  createRouteGroup: (body: { name: string; strategy: 'RING_ALL' | 'SEQUENTIAL'; ringSeconds: number; maxWaitSeconds: number; fallbackQueueId?: string; isActive?: boolean }) =>
    api<TelephonyRoutesState>('/telephony/browser/routes/groups', { method: 'POST', body: JSON.stringify(body) }),
  updateRouteGroup: (id: string, body: Record<string, unknown>) =>
    api<TelephonyRoutesState>(`/telephony/browser/routes/groups/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteRouteGroup: (id: string) =>
    api<TelephonyRoutesState>(`/telephony/browser/routes/groups/${id}`, { method: 'DELETE' }),
  updateRouteMembers: (id: string, memberIds: string[]) =>
    api<TelephonyRoutesState>(`/telephony/browser/routes/groups/${id}/members`, { method: 'PUT', body: JSON.stringify({ memberIds }) }),
  setRootRoute: (queueId: string) =>
    api<TelephonyRoutesState>('/telephony/browser/routes/root', { method: 'PUT', body: JSON.stringify({ queueId }) }),
  previewRoutes: () => api<TelephonyRoutePreview>('/telephony/browser/routes/preview'),
  applyRoutes: (draftRevision: number) => api<{ routes: TelephonyRoutesState; preview: TelephonyRoutePreview; pbx: unknown }>('/telephony/browser/routes/apply-confirmed', {
    method: 'POST',
    body: JSON.stringify({ draftRevision }),
  }),
  registration: (registered: boolean) => api('/telephony/browser/registration', {
    method: 'POST',
    body: JSON.stringify({ registered }),
  }),
  operatorStatus: (status: OperatorStatus) => api('/telephony/browser/status', {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  }),
  outbound: ({ title: _title, ...payload }: OutboundCallPayload) => api<BrowserCall>('/telephony/browser/outbound', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  registerSession: (body: { direction: 'INBOUND' | 'OUTBOUND'; phone: string; sessionId: string; callId?: string; anchorId?: string; externalUuid?: string; recovery?: boolean }) =>
    api<BrowserCall>('/telephony/browser/session', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  recoverActive: () => api<{ call: BrowserCall | null; recoveryStarted: boolean; message?: string }>('/telephony/browser/recovery', { method: 'POST' }),
  detached: (callId: string, keepalive = false) => api<{ recoverable: boolean; call: BrowserCall | null }>(
    `/telephony/browser/calls/${callId}/detached`,
    keepalive ? { method: 'POST', keepalive: true } : { method: 'POST' },
  ),
  answer: (callId: string) => api<BrowserCall>(`/telephony/browser/calls/${callId}/answer`, { method: 'POST' }),
  hold: (callId: string) => api<BrowserCall>(`/telephony/browser/calls/${callId}/hold`, { method: 'POST' }),
  resume: (callId: string) => api<BrowserCall>(`/telephony/browser/calls/${callId}/resume`, { method: 'POST' }),
  transfer: (callId: string, destination: string) => api<BrowserCall>(`/telephony/browser/calls/${callId}/transfer`, {
    method: 'POST',
    body: JSON.stringify({ destination }),
  }),
  finish: (callId: string, disposition: 'COMPLETED' | 'MISSED' | 'CANCELLED' | 'FAILED', reason = '') =>
    api<BrowserCall>(`/telephony/browser/calls/${callId}/finish`, {
      method: 'POST',
      body: JSON.stringify({ disposition, reason }),
    }),
  reportQuality: (callId: string, quality: MediaQualityReport | WebRtcMediaQuality) => api(`/telephony/browser/calls/${callId}/quality`, {
    method: 'POST',
    body: JSON.stringify({
      active: quality.active,
      codec: quality.codec,
      route: quality.route,
      protocol: quality.protocol,
      iceState: quality.iceState,
      connectionState: quality.connectionState,
      packetLossPercent: quality.packetLossPercent,
      outboundPacketLossPercent: quality.outboundPacketLossPercent,
      jitterMs: quality.jitterMs,
      outboundJitterMs: quality.outboundJitterMs,
      rttMs: quality.rttMs,
      concealmentPercent: quality.concealmentPercent,
    }),
  }),
  qualityDiagnostics: () => api<TelephonyQualityDiagnostics>('/telephony/browser/quality'),
  saveResult: (callId: string, payload: CallResultPayload) => api<BrowserCall>(`/telephony/browser/calls/${callId}/result`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  calls: (params: URLSearchParams, signal?: AbortSignal) => api<BrowserCall[]>(`/telephony/calls?${params.toString()}`, { signal }),
  callFilterOptions: () => api<CallHistoryFilterOptions>('/telephony/calls/filter-options'),
  callStatistics: (periodDays: 7 | 30 | 90) => api<CallStatistics>(`/telephony/call-statistics?period=${periodDays}d`),
  blacklist: () => api<TelephonyBlacklistRule[]>('/telephony/blacklist'),
  createBlacklistRule: (body: TelephonyBlacklistPayload) => api<TelephonyBlacklistRule>('/telephony/blacklist', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  updateBlacklistRule: (id: string, body: Partial<TelephonyBlacklistPayload>) => api<TelephonyBlacklistRule>(`/telephony/blacklist/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  deactivateBlacklistRule: (id: string) => api<TelephonyBlacklistRule>(`/telephony/blacklist/${id}`, { method: 'DELETE' }),
  call: (id: string) => api<BrowserCall>(`/telephony/calls/${id}`),
  retentionStatus: () => api<RecordingRetentionStatus>('/telephony/recordings/retention'),
  updateRetention: (body: UpdateRecordingRetentionPayload) => api<RecordingRetentionStatus>('/telephony/recordings/retention', {
    method: 'PUT',
    body: JSON.stringify(body),
  }),
  scanRetention: () => api<RecordingMaintenanceJob>('/telephony/recordings/retention/scan', { method: 'POST' }),
  runRetention: (confirmation: string) => api<RecordingMaintenanceJob>('/telephony/recordings/retention/run', {
    method: 'POST',
    body: JSON.stringify({ confirmation }),
  }),
  setRecordingImportance: (id: string, important: boolean) => api<{ callId: string; status: string; important: boolean }>(`/telephony/calls/${id}/recording/importance`, {
    method: 'PATCH',
    body: JSON.stringify({ important }),
  }),
  linkTicket: (id: string, ticketId: string) => api<BrowserCall>(`/telephony/calls/${id}/ticket`, { method: 'PATCH', body: JSON.stringify({ ticketId }) }),
  linkCallClient: (id: string, companyId: string, contactId?: string) => api<BrowserCall>(`/client-history/calls/${id}/client`, {
    method: 'PATCH',
    body: JSON.stringify(callClientLinkPayload(companyId, contactId)),
  }),
  selectBrowserTicket: (id: string, ticketId: string) => api<BrowserCall>(`/telephony/browser/calls/${id}/ticket`, {
    method: 'PATCH',
    body: JSON.stringify({ ticketId }),
  }),
  contacts: () => api<PhoneContact[]>('/contacts'),
  missedCalls: (status = '') => api(`/telephony/missed${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  updateMissedCall: (id: string, body: Record<string, unknown>) => api(`/telephony/missed/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  callbacks: () => api('/telephony/callbacks'),
  updateCallback: (id: string, body: Record<string, unknown>) => api(`/telephony/callbacks/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
};
